<template>
  <div class="vz-special-requirement-card">
    <div v-if="orderLock && !isCloned" class="disabled-card"/>
    <div class="text-h1 vz-special-requirement-card-title">
      {{ titleCard }}
    </div>
    <div class="vz-special-requirement-card-list">
      <div>
        <vz-checkbox-collapse
          v-if="!simple"
          v-model="driverDataActive"
          :disabled="orderLock && !isCloned"
          collapsible
          label="Данные водителя (для пропуска)"
          :tooltip="driverDataTooltip"
          class="vz-driver-data"
        >
          <template #header-additional>
            <div class="vz-driver-data-pass">
              <vz-toggler
                v-model="driverPass"
                variant="primary"
              >
                За сутки
                <vz-tooltip-info
                  text="Данные водителя будут направлены Вам до 17 часов дня, предшествующего дню забора/доставки"/>
              </vz-toggler>
            </div>
          </template>
          <div
            v-if="driverPassNote && !driverPass"
            class="active fs-12 color-primary mb-10"
          >
            {{ driverPassNote }}
          </div>
          <vz-input
            v-model.trim="driverDataValue"
            :rules="[formRules.emailOrPhone, formRules.required]"
            label="E-mail или телефон"
            required
          />

          <div class="text-caption color-light vz-driver-email-hint">
            Укажите E-mail или телефон, на который будут направлены контактные данные водителя
          </div>
        </vz-checkbox-collapse>
        <vz-checkbox-collapse
          v-if="!simple"
          v-model="roadmapActive"
          :disabled="orderLock && !isCloned"
          collapsible
          label="Добавить схему проезда"
        >
          <vz-special-requirement-roadmap
            v-model:value="roadmapValue"
            v-model:active="roadmapActive"
            :old-active="roadmapOldActive"
            :directionTo="directionTo"
          />
        </vz-checkbox-collapse>
      </div>
      <hr v-if="!simple"/>
      <div>
        <vz-checkbox-collapse
          v-if="!simple"
          v-model="entryFee"
          :disabled="orderLock && !isCloned"
          collapsible
          label="Платный въезд на территорию"
        >
          <vz-input
            v-model="entryFeeValue"
            :icon-url="iconPath?.url"
            icon-left
            type="number"
            :label="`Сумма, ${getCurrencyText(currentLocation?.country)}`"
            @change="orderStore.getOrderPriceAction()"
          />
        </vz-checkbox-collapse>
        <vz-checkbox-collapse
          v-model="unloadingOperationsActive"
          :disabled="orderLock && !isCloned"
          collapsible
          :label="unloadingOperationsTitle"
        >
          <div class="vz-unloading-operations flex-center">
            <vz-measure-input
              v-model="unloadingOperationsFloor"
              label="Этаж"
              class="small-width"
              placeholder="1"
              big
              :max-value="9999"
              :min-value="1"
              :allow-decimal="false"
              @change="onUnloadingOperationsFloorChange"
            />
            <vz-tooltip
              v-if="overMaxFloor"
            >
              <vz-icon
                name="alert-circle-outline"
                variant="primary"
                :size="16"
              />
              <template #balloon>
                <span>Возможно Вы неверно указали этаж! Рекомендуем проверить введённое значение</span>
              </template>
            </vz-tooltip>
            <vz-checkbox
              v-model="unloadingOperationsFreightElevator"
              label="Грузовой лифт"
            />
          </div>
        </vz-checkbox-collapse>
        <vz-checkbox-collapse
          v-if="!simple"
          v-model="documentAddressActive"
          :disabled="orderLock && !isCloned"
          collapsible
          label="Документы по адресу"
        >
          <address-autocomplete
            v-model="documentAddressValue"
            :location="currentLocation"
            :rules="[formRules.required]"
            big
            use-map
            class="mb-10 mt-5"
            @map-submit="onMapSubmit"
          ></address-autocomplete>
          <div class="color-light text-caption mt-5">
            Расстояние между адресами не должно превышать 1 км
          </div>
        </vz-checkbox-collapse>
        <vz-checkbox
          v-if="!simple"
          v-model="hasSpecificLoading"
          :disabled="orderLock && !isCloned"
          label="Спец.требования и транспорт"
          manual
          @change="showModal"
        />
        <vz-specific-loading-review
          v-if="!simple"
          :items="specificLoadingReviewItems"
          :direction-to="directionTo"
          @remove="removeSpecificLoading"
          @save="saveSpecificLoading"
        />
      </div>
      <hr class="mt-0"/>
      <vz-checkbox-collapse
        v-model="driverCommentActive"
        :disabled="orderLock && !isCloned"
        collapsible
        label="Комментарий водителю"
      >
        <vz-textarea
          v-model="driverCommentValue"
          autosize
          class="vz-driver-comment-value"
          :max-length="200"
        />
      </vz-checkbox-collapse>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useOrderStore} from '@/stores/order';
import {BASE_URL_HTTPS} from '@/composables/useVzFetch'
import {getOrderFloorByZone} from '@/api/order';
import {OrderSpecialRequirement} from '@/types/types';
import {storeToRefs} from 'pinia';
import {useCityStore} from '@/stores/city';
import {asyncComputed} from '@vueuse/core';
import {getAddressByGuid} from '~/api/city';
import formRules from '@/common/vz-form-rules';
import AddressAutocomplete from '@/components/autocomplete/address-autocomplete.vue';
import VzCheckboxCollapse from '~/components/ui-kit/vz-checkbox-collapse.vue';
import VzToggler from '~/components/ui-kit/vz-toggler.vue';
import VzTextarea from '~/components/ui-kit/vz-textarea.vue';
import VzCheckbox from '~/components/ui-kit/vz-checkbox.vue';
import VzInput from '~/components/ui-kit/vz-input.vue';
import VzIcon from '~/components/ui-kit/vz-icon.vue';
import VzTooltip from '~/components/ui-kit/vz-tooltip.vue';
import VzMeasureInput from '@/components/ui-kit/vz-measure-input.vue';
import VzSpecialRequirementRoadmap
  from '@/components/personal/order/special-requirements/vz-special-requirement-roadmap.vue'
import VzSpecificLoadingReview from '@/components/personal/order/special-requirements/vz-specific-loading-review.vue';
import {removeFile} from '~/api/backend';
import VzTooltipInfo from '~/components/ui-kit/vz-tooltip-info.vue';
import {getCurrencyText, phoneCountries} from "~/common/objects";

const props = defineProps({
  titleCard: {
    type: String,
    default: ''
  },
  orderLock: {
    type: Boolean,
    default: false
  },
  directionTo: {
    type: Boolean,
    default: false
  },
  simple: {
    type: Boolean,
    default: false,
  }
})
const {$showDialog} = useNuxtApp()
const {rclCityList} = storeToRefs(useCityStore())
const orderStore = useOrderStore()
const driverDataTooltip = 'Обращаем Ваше внимание на необходимость строгого соблюдения конфиденциальности полученной ' +
  'Вами информации (сведений о паспортных данных и иных данных представителя экспедитора – водителя транспортного средства) ' +
  'и обеспечения доступа к такой информации только тем лицам, в чьи должностные обязанности входит работа с такой информацией. ' +
  'Указывая адрес электронной почты, Вы автоматически соглашаетесь с перечисленными условиями.'

const {locationMaxFloor, destinationLocation, dispatchLocation} = storeToRefs(orderStore)

const isCloned = computed(() => {
  return !!useRoute().query.clone
})

const directionName = computed(() => {
  return props.directionTo ? 'destination' : 'dispatch'
})

const modalName = computed(() => {
  return `specific-loading-${directionName.value}`
})

const currentLocation = asyncComputed(async () => {
  const guid = orderStore.orderForm.directions[directionName.value].locationGuid
  return rclCityList.value.find(city => city.guid === guid) || (await getAddressByGuid(guid)).location
})

const locationZoneGuid = computed(() => {
  return (props.directionTo ? destinationLocation.value?.zone_guid : dispatchLocation.value?.zone_guid) || ''
})

const unloadingOperationsTitle = computed(() => {
  return props.directionTo ? 'Разгрузочные работы' : 'Погрузочные работы'
})

const unloadingOperationsActive = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].unloadingOperations.active
  },
  async set(data: boolean) {
    if (data) {
      await updateUnloadingOperationsMaxFloor(locationZoneGuid.value)
    }

    orderStore.orderForm.specialRequirements[directionName.value].unloadingOperations.active = data
    orderStore.getOrderPriceAction()
  }
})

const unloadingOperationsFloor = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].unloadingOperations.floor
  },
  set(data: number) {
    orderStore.orderForm.specialRequirements[directionName.value].unloadingOperations.floor = data
  }
})

const unloadingOperationsFreightElevator = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].unloadingOperations.freightElevator
  },
  set(data: boolean) {
    orderStore.orderForm.specialRequirements[directionName.value].unloadingOperations.freightElevator = data
    orderStore.getOrderPriceAction()
  }
})

const driverCommentActive = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].driverComment.active
  },
  set(data: boolean) {
    orderStore.orderForm.specialRequirements[directionName.value].driverComment.active = data
  }
})

const driverCommentValue = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].driverComment.value
  },
  set(data: string) {
    orderStore.orderForm.specialRequirements[directionName.value].driverComment.value = data
  }
})

const roadmapActive = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].roadmap.active
  },
  set(data: boolean) {
    if (!data) {
      onRemoveRoadmap()
      orderStore.orderForm.specialRequirements[directionName.value].roadmap.value = ''
      orderStore.orderForm.specialRequirements[directionName.value].roadmap.oldActive = false
    }
    orderStore.orderForm.specialRequirements[directionName.value].roadmap.active = data
  }
})

const entryFee = computed({
  get() {
    return !!orderStore.orderForm.specialRequirements[directionName.value].entryFee?.active
  },
  set(data: boolean) {
    orderStore.orderForm.specialRequirements[directionName.value].entryFee.active = data
    entryFeeChangeCheckbox()
  }
})

const entryFeeValue = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].entryFee.value
  },
  set(data: number | string) {
    orderStore.orderForm.specialRequirements[directionName.value].entryFee.value = +data || ''
  }
})

const roadmapValue = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].roadmap.value
  },
  set(data: string) {
    orderStore.orderForm.specialRequirements[directionName.value].roadmap.value = !!data.length ? `${BASE_URL_HTTPS}${data}` : ''
  }
})

const roadmapOldActive = computed(() => {
  return orderStore.orderForm.specialRequirements[directionName.value].roadmap.oldActive
})

const driverDataActive = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].driverData.active
  },
  set(data: boolean) {
    orderStore.orderForm.specialRequirements[directionName.value].driverData.active = data
    if (!data && driverPass.value) {
      driverPass.value = false
    }
  }
})

const driverPass = computed({
  get() {
    return !!orderStore.orderForm.specialRequirements[directionName.value].driverData.pass
  },
  set(value: boolean) {
    orderStore.orderForm.specialRequirements[directionName.value].driverData.pass = value
    if (value) {
      driverDataActive.value = true
      orderStore.getOrderPriceAction()
    }
  }
})

const driverPassNote = computed(() => {
  return orderStore.orderForm.specialRequirements[directionName.value].driverData.passNote
})

const driverDataValue = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].driverData.value
  },
  set(data: string) {
    orderStore.orderForm.specialRequirements[directionName.value].driverData.value = data
  }
})

const documentAddressActive = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].documentAddress.active
  },
  set(data: boolean) {
    orderStore.orderForm.specialRequirements[directionName.value].documentAddress.active = data
  }
})

const documentAddressValue = computed({
  get() {
    return orderStore.orderForm.specialRequirements[directionName.value].documentAddress.value
  },
  set(data: string) {
    orderStore.orderForm.specialRequirements[directionName.value].documentAddress.value = data
    orderStore.getOrderPriceAction()
  }
})

const componentDirection = computed(() => {
  const {
    directions: {dispatch, destination}
  } = orderStore.orderForm
  return props.directionTo ? destination : dispatch
})

const specificLoadingReviewItems = computed(() => {
  const specificLoading = orderStore.orderForm.specialRequirements[directionName.value]
  const data: { code: string }[] = []
  for (const key in specificLoading) {
    if (typeof specificLoading[key] === 'boolean' && specificLoading[key] === true) {
      data.push({code: key})
    }
  }
  if (specificLoading.type !== 'default') {
    data.push({code: specificLoading.type})
  }
  return data
})

const hasSpecificLoading = computed(() => {
  return specificLoadingReviewItems.value.length > 0
})

const iconPath = computed(() => {
  return phoneCountries.find((val) => {
    return val.value === currentLocation.value?.country
  })
})

const overMaxFloor = computed(() => {
  return locationMaxFloor.value[directionName.value] === 0 ? false : (Number(unloadingOperationsFloor.value) > Number(locationMaxFloor.value[directionName.value]))
})

async function entryFeeChangeCheckbox() {
  if(entryFeeValue.value) {
    orderStore.getOrderPriceAction()
  }
}

function onUnloadingOperationsFloorChange() {
  orderStore.getOrderPriceAction()
}

async function updateUnloadingOperationsMaxFloor(zoneGuid: string) {
  const data = await getOrderFloorByZone(zoneGuid)
  locationMaxFloor.value[directionName.value] = Number(data.floor?.[0] || 0)
}

function showModal() {
  $showDialog({name: modalName.value})
}

function onMapSubmit({address}: { address: string }) {
  documentAddressValue.value = address
}

function saveSpecificLoading(data: OrderSpecialRequirement) {
  orderStore.orderForm.specialRequirements[directionName.value] = data
  orderStore.getOrderPriceAction()
}

function removeSpecificLoading(code: string) {
  const transportTypeCodes = ['lateralLoad', 'topLoad', 'tailLift', 'manipulator', 'openMachine', 'removableCurtains']
  orderStore.orderForm.specialRequirements[directionName.value][code] = false
  if (transportTypeCodes.includes(code)) {
    orderStore.orderForm.specialRequirements[directionName.value].type = 'default'
  }
  orderStore.getOrderPriceAction()
}

async function updateCurrentLocation(): Promise<void> {
  const locations = [...rclCityList.value]
  const currentLocation = locations.find(c => c.guid === orderStore.orderForm.directions[directionName.value].locationGuid)
  if (!currentLocation) {
    const {location} = await getAddressByGuid(orderStore.orderForm.directions[directionName.value].locationGuid)
    locations.unshift(location)
    orderStore.orderForm.directions[directionName.value].locationGuid = location.guid
  } else {
    orderStore.orderForm.directions[directionName.value].locationGuid = currentLocation.guid
  }
}

async function onRemoveRoadmap() {
  const data = orderStore.orderForm.specialRequirements[directionName.value].roadmap?.value?.split('/').pop()
  if (data) {
    await removeFile({type: 'roadmap', url: `/upload/roadmap/${data}`})
  }
}

watch(() => locationZoneGuid.value, (): void => {
  if (unloadingOperationsActive.value) {
    updateUnloadingOperationsMaxFloor(locationZoneGuid.value)
  }

  if (currentLocation.value?.guid !== orderStore.orderForm.directions[directionName.value].locationGuid) {
    updateCurrentLocation()
  }
}, {immediate: true})
</script>
